import React from 'react';
import SEO from '../components/SEO';

const CoreJava: React.FC = () => {
  return (
    <>
      <SEO 
        title="Core Java Training | Java Programming Fundamentals Course with Certification - SBRIT Solution"
        description="Master Core Java programming fundamentals with OOP concepts, collections, multithreading, and exception handling. Complete beginner-friendly Java course with hands-on projects and placement support."
        keywords="core java training, java programming course, java fundamentals, java certification, java for beginners, object oriented programming java, java collections framework"
        url="https://sbritsolution.in/courses/core-java"
        type="article"
      />
      
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-red-600 to-orange-600 text-white py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Core Java Training
              </h1>
              <p className="text-xl md:text-2xl mb-8 leading-relaxed">
                Master Java Programming Fundamentals | OOP Concepts | Collections | Multithreading
              </p>
              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <button className="bg-yellow-500 hover:bg-yellow-600 text-black px-8 py-4 rounded-lg font-semibold text-lg transition-colors">
                  Start Learning - ₹12,999
                </button>
                <button className="border-2 border-white hover:bg-white hover:text-red-600 px-8 py-4 rounded-lg font-semibold text-lg transition-colors">
                  Download Syllabus
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Why Core Java */}
        <div className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Why Learn Core Java?</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center p-6 rounded-lg shadow-lg">
                <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🏗️</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">Foundation Language</h3>
                <p className="text-gray-600">Core Java is the foundation for all Java technologies including Spring, Android, and enterprise applications.</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg">
                <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">💼</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">High Demand</h3>
                <p className="text-gray-600">Java developers are in high demand with excellent career opportunities in IT industry.</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg">
                <div className="bg-yellow-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🌍</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">Platform Independent</h3>
                <p className="text-gray-600">Write once, run anywhere - Java applications work on any platform with JVM.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Core Java Curriculum */}
        <div className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Complete Core Java Curriculum</h2>
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-4 text-red-600">Java Fundamentals</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Java Introduction & History</li>
                    <li>• JDK, JRE, JVM Architecture</li>
                    <li>• Data Types & Variables</li>
                    <li>• Operators & Control Statements</li>
                    <li>• Loops & Decision Making</li>
                    <li>• Arrays & String Handling</li>
                    <li>• Methods & Parameter Passing</li>
                    <li>• Package & Access Modifiers</li>
                  </ul>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-4 text-orange-600">Object-Oriented Programming</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Classes & Objects</li>
                    <li>• Constructors & Destructors</li>
                    <li>• Inheritance & Polymorphism</li>
                    <li>• Abstraction & Encapsulation</li>
                    <li>• Method Overloading & Overriding</li>
                    <li>• Abstract Classes & Interfaces</li>
                    <li>• Static & Final Keywords</li>
                    <li>• Inner Classes & Anonymous Classes</li>
                  </ul>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-4 text-blue-600">Advanced Core Java</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Exception Handling</li>
                    <li>• Collections Framework</li>
                    <li>• Generics & Wildcards</li>
                    <li>• Multithreading & Synchronization</li>
                    <li>• File I/O Operations</li>
                    <li>• Serialization & Deserialization</li>
                    <li>• Reflection API</li>
                    <li>• Annotations & Lambda Expressions</li>
                  </ul>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-4 text-green-600">Practical Applications</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Console-based Applications</li>
                    <li>• File Management Systems</li>
                    <li>• Banking Application Project</li>
                    <li>• Student Management System</li>
                    <li>• Library Management Project</li>
                    <li>• Inventory Management System</li>
                    <li>• Calculator & Utility Programs</li>
                    <li>• Data Structure Implementation</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Learning Path */}
        <div className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">3-Month Learning Journey</h2>
            <div className="max-w-4xl mx-auto">
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-gradient-to-b from-red-50 to-red-100 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold mb-3 text-red-700">Month 1: Basics</h3>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Java Environment Setup</li>
                    <li>• Basic Syntax & Data Types</li>
                    <li>• Control Structures</li>
                    <li>• Arrays & Methods</li>
                    <li>• String Manipulation</li>
                  </ul>
                </div>
                <div className="bg-gradient-to-b from-orange-50 to-orange-100 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold mb-3 text-orange-700">Month 2: OOP Concepts</h3>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Classes & Objects</li>
                    <li>• Inheritance & Polymorphism</li>
                    <li>• Abstract Classes & Interfaces</li>
                    <li>• Exception Handling</li>
                    <li>• Package Management</li>
                  </ul>
                </div>
                <div className="bg-gradient-to-b from-yellow-50 to-yellow-100 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold mb-3 text-yellow-700">Month 3: Advanced Topics</h3>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Collections Framework</li>
                    <li>• Multithreading</li>
                    <li>• File I/O Operations</li>
                    <li>• Project Development</li>
                    <li>• Interview Preparation</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Career Opportunities */}
        <div className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Career Opportunities After Core Java</h2>
            <div className="max-w-4xl mx-auto">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-4">Entry-Level Positions</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Junior Java Developer</li>
                    <li>• Software Developer Trainee</li>
                    <li>• Java Programmer</li>
                    <li>• Application Developer</li>
                    <li>• Backend Developer</li>
                    <li>• Software Engineer</li>
                  </ul>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-4">Salary Expectations</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Fresher: ₹3-6 LPA</li>
                    <li>• 1-2 Years: ₹6-10 LPA</li>
                    <li>• 3-5 Years: ₹10-18 LPA</li>
                    <li>• Senior: ₹18+ LPA</li>
                    <li>• MNCs: Higher packages</li>
                    <li>• Remote opportunities available</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Course Details */}
        <div className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Core Java Course Details</h2>
            <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="border-l-4 border-red-500 pl-4">
                  <h3 className="font-semibold text-lg">Duration</h3>
                  <p className="text-gray-600">3 Months (Comprehensive Program)</p>
                </div>
                <div className="border-l-4 border-orange-500 pl-4">
                  <h3 className="font-semibold text-lg">Mode</h3>
                  <p className="text-gray-600">Online Live Classes + Practice Labs</p>
                </div>
                <div className="border-l-4 border-yellow-500 pl-4">
                  <h3 className="font-semibold text-lg">Prerequisites</h3>
                  <p className="text-gray-600">Basic computer knowledge (No programming experience required)</p>
                </div>
              </div>
              <div className="space-y-6">
                <div className="border-l-4 border-green-500 pl-4">
                  <h3 className="font-semibold text-lg">Investment</h3>
                  <p className="text-gray-600">₹12,999 (EMI options available)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <h3 className="font-semibold text-lg">Certification</h3>
                  <p className="text-gray-600">Core Java Certification + Project Portfolio</p>
                </div>
                <div className="border-l-4 border-purple-500 pl-4">
                  <h3 className="font-semibold text-lg">Support</h3>
                  <p className="text-gray-600">24/7 doubt clearing & career guidance</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="py-16 bg-gradient-to-r from-red-600 to-orange-600 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-6">Start Your Java Programming Journey Today!</h2>
            <p className="text-xl mb-8">Master Core Java and build a strong foundation for your programming career!</p>
            <div className="flex flex-col md:flex-row gap-4 justify-center">
              <button className="bg-yellow-500 hover:bg-yellow-600 text-black px-8 py-4 rounded-lg font-semibold text-lg transition-colors">
                Enroll Now - ₹12,999
              </button>
              <button className="border-2 border-white hover:bg-white hover:text-red-600 px-8 py-4 rounded-lg font-semibold text-lg transition-colors">
                Free Demo Class
              </button>
            </div>
            <p className="mt-4 text-sm opacity-90">Next batch starts: 12th August 2024 | Limited seats available!</p>
          </div>
        </div>

        {/* Related Courses */}
        <div className="py-12 bg-white">
          <div className="container mx-auto px-4">
            <h3 className="text-2xl font-bold text-center mb-8">Advance Your Java Skills</h3>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/courses/advanced-java" className="bg-red-100 hover:bg-red-200 px-4 py-2 rounded-lg text-red-700 font-medium transition-colors">Advanced Java</a>
              <a href="/courses/spring-boot" className="bg-green-100 hover:bg-green-200 px-4 py-2 rounded-lg text-green-700 font-medium transition-colors">Spring Boot</a>
              <a href="/courses/full-stack-java" className="bg-blue-100 hover:bg-blue-200 px-4 py-2 rounded-lg text-blue-700 font-medium transition-colors">Full Stack Java</a>
              <a href="/java-training-online" className="bg-orange-100 hover:bg-orange-200 px-4 py-2 rounded-lg text-orange-700 font-medium transition-colors">Java Training Online</a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CoreJava;
